import { buildTranslate } from 'locales'

// Generic URL type for link handling
export type UrlData = {
  type: 'no_link' | 'external' | 'inapp'
  external?: string
  inapp?: string
}

// URL types and values for UrlPicker components
export enum UrlType {
  NoLink = 'no_link',
  External = 'external',
  Inapp = 'inapp',
}

export type UrlValue = {
  inapp: string
  external: string
  fullUrl?: string
  type: UrlType
  authenticable: boolean
  queryParams?: string
}

// Use the generic content translations directly
const t = buildTranslate('cms.content.link_locations')

export const getUrlType = (url: UrlData) => {
  if (url.type === 'no_link') return t('no_link')
  if (url.type === 'external') return `${t('external_url')}:`
  if (url.type === 'inapp') return `${t('page_within_app')}:`
  return 'No link'
}

export const getUrlDisplay = (url: UrlData) => {
  if (url.type === 'no_link') return 'No link'
  if (url.type === 'external') return url.external || ''
  if (url.type === 'inapp') return url.inapp || ''
  return ''
}

// Helper function to get displayed URL for external links
export function getDisplayedUrl(url: string, authenticable: boolean): string {
  if (
    authenticable &&
    /^https:\/\/auth.thanx(.*?).com/.test(url) &&
    url.indexOf('redirect_uri') > -1
  ) {
    const redirectUri = new URL(url).searchParams.get('redirect_uri')
    if (redirectUri) return redirectUri
  }

  return url
}

// Consolidated URL parsing logic from Cms/helper.tsx
export function getUrlValue(
  url: string = '',
  authenticable: boolean = false
): UrlValue {
  let type = UrlType.NoLink
  let queryParams: string | undefined

  if (url.startsWith('thanx://')) {
    type = UrlType.Inapp
    queryParams = url.split('?')[1]
  } else if (url) {
    type = UrlType.External
  }

  const inappUrl = type === UrlType.Inapp ? url.split('?')[0] : ''

  return {
    inapp: inappUrl,
    external:
      type === UrlType.External ? getDisplayedUrl(url, authenticable) : '',
    fullUrl: url,
    type,
    authenticable,
    queryParams,
  }
}

// Function to extract URL string from UrlValue (similar to getUrlAddress in AppHomePage helpers)
export function getUrlAddress(urlValue: UrlValue): string {
  if (urlValue.type === UrlType.NoLink) {
    return ''
  }

  let urlAddress = urlValue[urlValue.type]

  // For ordering-menu URLs, append query parameters if they exist
  if (
    urlValue.type === UrlType.Inapp &&
    urlValue.inapp.startsWith('thanx://ordering-menu') &&
    urlValue.queryParams?.split('category=')[1]
  ) {
    urlAddress += `?${urlValue.queryParams}`
  }

  return urlAddress
}

// For navigation tabs, we can create a simple version since we just have a string link
export const getNavigationTabLinkDisplay = (
  link: string
): { type: string; display: string } => {
  // For now, assume all navigation tab links are internal app pages
  return {
    type: `${t('page_within_app')}:`,
    display: link,
  }
}
